import { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Spin } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import {
  patientGetCompany,
  patientGetDocumentById,
} from '../../http/patientPortal';
import { DocumentReport } from '../../shared/reports';
import { ICompany, IDocument } from '../../shared/interfaces';

const PatientDocumentPrint: React.FC = () => {
  const { tenant, id } = useParams<{ tenant?: string; id?: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [report, setReport] = useState<IDocument | null>(null);
  const [company, setCompany] = useState<ICompany | null>(null);
  const [signature, setSignature] = useState<string | null>(null);

  const loginPath = tenant ? `/${tenant}/portal` : '/portal';
  const panelPath = tenant ? `/${tenant}/portal/painel` : '/portal/painel';

  const loadReport = useCallback(async () => {
    const token = sessionStorage.getItem('@Sigeclin:patientToken');
    const patientTenant = sessionStorage.getItem('@Sigeclin:patientTenant');

    if (!token || !id) {
      navigate(loginPath, { replace: true });
      return;
    }

    if (patientTenant) {
      sessionStorage.setItem('@Sigeclin:clinic', patientTenant);
    }

    try {
      setLoading(true);
      const [data, companyData] = await Promise.all([
        patientGetDocumentById(id),
        patientGetCompany(),
      ]);
      setReport(data);
      setCompany(companyData);

      const isDigitallySigned = Boolean(
        data.signature_validation_code || data.signed_at,
      );
      setSignature(
        isDigitallySigned ? data.professional_signature || null : null,
      );
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ??
        'Não foi possível carregar o documento para impressão.';
      setError(Array.isArray(msg) ? msg[0] : msg);
    } finally {
      setLoading(false);
    }
  }, [id, loginPath, navigate]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  if (loading) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Spin size="large" />
      </div>
    );
  }

  if (error || !report) {
    return (
      <div style={{ maxWidth: 880, margin: '20px auto', padding: '0 16px' }}>
        <Alert
          type="error"
          showIcon
          message={error ?? 'Documento não encontrado.'}
          action={
            <Button type="link" onClick={() => navigate(panelPath)}>
              Voltar ao painel
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div style={{ padding: '16px' }}>
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>
        <DocumentReport
          report={report}
          signature={signature}
          validationCode={report.signature_validation_code || null}
          companyOverride={company}
          clinicOverride={sessionStorage.getItem('@Sigeclin:patientTenant')}
        />
      </div>
    </div>
  );
};

export default PatientDocumentPrint;
